import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import {
  useWalkthroughStore,
  type WalkthroughStep,
} from '../../store/walkthroughStore';
import {
  areAutoToursEnabled,
  areToursEnabled,
  isTourDone,
  markTourDone,
} from './tourPrefs';
import {
  getBuiltInTour,
  resolveTourProfile,
  type TourProfile,
} from './tourRegistry';
import { capturePageMap, pageTourId } from './capturePageMap';
import { ensureTour, fetchCachedTour, regenerateTour } from './tourApi';
import {
  isCockpitEmbed,
  isEmbedPanelActive,
  waitUntilEmbedPanelActive,
} from './tourEnv';
import { useTourPrepStore } from './tourPrepStore';

import { useDevStore } from '../../store/useDevStore';

function samePath(a: string, b: string) {
  return (a.replace(/\/+$/, '') || '/') === (b.replace(/\/+$/, '') || '/');
}

/**
 * Prefetch async: cache → senão captura + envia (pode trocar de página).
 * Banner no topo confirma envio; ícone ? vira pronto quando o servidor terminar.
 */
export function useAutoTourOnce() {
  const location = useLocation();
  const perfil = useAuthStore((s) => s.perfil);
  const cargo = useAuthStore((s) => s.cargo);
  const user = useAuthStore((s) => s.user);
  const startWalkthrough = useWalkthroughStore((s) => s.startWalkthrough);
  const { settings } = useDevStore();

  const setPreparing = useTourPrepStore((s) => s.setPreparing);
  const setReady = useTourPrepStore((s) => s.setReady);
  const setError = useTourPrepStore((s) => s.setError);
  const showBannerSent = useTourPrepStore((s) => s.showBannerSent);

  useEffect(() => {
    // Se o tour estiver desativado globalmente pelo desenvolvedor, encerra o fluxo
    if (!settings.showTour) return;

    // Se o auto start estiver desativado ou desabilitado localmente
    if (!settings.autoStartTour || !areAutoToursEnabled()) return;

    // Apenas inicia tour automático se for o primeiro acesso do usuário (ultimoLogin === null)
    if (user && 'ultimoLogin' in user && user.ultimoLogin !== null) {
      return;
    }

    const path = location.pathname;
    const profile = resolveTourProfile(perfil, cargo);
    const tourId = pageTourId(path);

    const already = useTourPrepStore.getState().get(path);
    if (already?.status === 'ready' && already.passos?.length) {
      return;
    }
    if (already?.status === 'preparing') return;

    let disposed = false;

    const tryPlayIfStillHere = (
      passos: WalkthroughStep[],
      pageResenha?: string | null,
    ) => {
      if (disposed) return;
      if (!samePath(window.location.pathname, path)) return;
      if (!areToursEnabled()) return;
      if (isTourDone(profile, tourId)) return;
      if (useWalkthroughStore.getState().isActive) return;
      if (isCockpitEmbed() && !isEmbedPanelActive()) return;
      if (!passos?.length) return;
      startWalkthrough(passos, { pageResenha });
      markTourDone(profile, tourId);
    };

    const timer = window.setTimeout(async () => {
      try {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          return;
        }
      } catch {
        /* ignore */
      }

      if (isCockpitEmbed() && !isEmbedPanelActive()) {
        const ok = await waitUntilEmbedPanelActive();
        if (!ok || disposed) return;
      }

      // Cache hit
      try {
        const cached = await fetchCachedTour(profile, path, 'APP');
        if (cached?.passos?.length) {
          setReady(path, {
            passos: cached.passos,
            pageResenha: cached.pageResenha,
            fonte: cached.fonte,
          });
          tryPlayIfStillHere(cached.passos, cached.pageResenha);
          if (cached.fonte === 'NAV' && areToursEnabled()) {
            void processPageInBackground({
              path,
              profile,
              cargo,
              pageTitle: undefined,
              forceRegen: true,
            });
          }
          return;
        }
      } catch {
        /* generate */
      }

      // Sem cache: captura local → aviso de envio → servidor processa (não cancela ao mudar de página)
      setPreparing(path);
      try {
        const capture = await capturePageMap({
          withScreenshot: true,
          ambiente: 'APP',
        });

        // Já enviável: usuário pode sair
        showBannerSent(path, capture.pageTitle);
        setPreparing(path);

        // Fire-and-forget: continua mesmo se trocar de rota
        void processCaptureOnServer({
          path,
          profile,
          cargo,
          capture,
          tryPlayIfStillHere,
        });
      } catch {
        if (!disposed) setError(path);
      }
    }, 650);

    return () => {
      disposed = true;
      window.clearTimeout(timer);
      // NÃO aborta processCaptureOnServer — o ponto é poder mudar de página
    };
  }, [
    location.pathname,
    perfil,
    cargo,
    startWalkthrough,
    setPreparing,
    setReady,
    setError,
    showBannerSent,
  ]);
}

async function processCaptureOnServer(input: {
  path: string;
  profile: TourProfile;
  cargo: string | null | undefined;
  capture: Awaited<ReturnType<typeof capturePageMap>>;
  tryPlayIfStillHere: (
    passos: WalkthroughStep[],
    pageResenha?: string | null,
  ) => void;
}) {
  const { path, profile, cargo, capture, tryPlayIfStillHere } = input;
  const setReady = useTourPrepStore.getState().setReady;
  const setError = useTourPrepStore.getState().setError;
  const showBannerReady = useTourPrepStore.getState().showBannerReady;

  try {
    let result = await ensureTour({
      perfil: profile,
      urlPath: path,
      cargo,
      pageTitle: capture.pageTitle,
      regions: capture.regions,
      elements: capture.elements,
      contentHash: capture.contentHash,
      screenshotBase64: capture.screenshotBase64,
      pageHtml: capture.pageHtml,
      ambiente: 'APP',
    });

    if (
      (!result.passos?.length || result.fonte === 'NAV') &&
      capture.regions.length
    ) {
      result = await regenerateTour({
        perfil: profile,
        urlPath: path,
        cargo,
        pageTitle: capture.pageTitle,
        regions: capture.regions,
        elements: capture.elements,
        contentHash: capture.contentHash,
        screenshotBase64: capture.screenshotBase64,
        pageHtml: capture.pageHtml,
        ambiente: 'APP',
      });
    }

    let steps = result.passos || [];
    if (!steps.length) {
      steps = getBuiltInTour(profile, path)?.steps || [];
    }
    if (!steps.length) {
      setError(path);
      return;
    }

    setReady(path, {
      passos: steps,
      pageResenha: result.pageResenha,
      fonte: result.fonte,
    });
    showBannerReady(path, capture.pageTitle);
    tryPlayIfStillHere(steps, result.pageResenha);
  } catch {
    setError(path);
  }
}

async function processPageInBackground(input: {
  path: string;
  profile: TourProfile;
  cargo: string | null | undefined;
  pageTitle?: string;
  forceRegen: boolean;
}) {
  try {
    const capture = await capturePageMap({
      withScreenshot: true,
      ambiente: 'APP',
    });
    useTourPrepStore
      .getState()
      .showBannerSent(input.path, capture.pageTitle || input.pageTitle);
    await processCaptureOnServer({
      path: input.path,
      profile: input.profile,
      cargo: input.cargo,
      capture,
      tryPlayIfStillHere: () => {
        /* upgrade silencioso — não força replay */
      },
    });
  } catch {
    /* silencioso */
  }
}
